import Spec from "csi/core/Spec";
import { organizeComponentItems } from "csi/middleware/common";
import CustomViewAttributesStore from "csi/middleware/customView/attributes/CustomViewAttributesStore";
import { C8AttributeKey } from "csi/typings";
import { DndItemWithId } from "designSystem/dnd/index";
import { BLANK_COMPONENTITEM, ComponentItem } from "designSystem/types/component-item-types";
import { isEqual } from "lodash";
import { IObservableArray, action, computed, makeObservable, observable } from "mobx";
import { ChangeEvent, createContext, useContext } from "react";
import { DropTargetMonitor } from "react-dnd";
import { ICustomViewGroup } from "src/c8-platform/type/customView";

// dnd attribute types for drag and drop
export enum DND_TYPE {
	GROUPING_LEVEL = "GROUPING_LEVEL",
}

export class GroupingLevel implements DndItemWithId {
	@observable.ref item: ComponentItem = BLANK_COMPONENTITEM;

	@observable _expand: boolean = false;

	@action setExpandByDefault = (value: boolean): void => {
		this._expand = value;
	};

	/** unique id */
	id: string;

	get expand(): boolean {
		return this._expand;
	}

	constructor() {
		makeObservable(this);
		this.id = `groupingLevel_${new Date().getTime()}`;
	}
}

export default class CustomViewGroupingStore {
	readonly attributeStore: CustomViewAttributesStore;

	_options: ComponentItem[] = observable.array<ComponentItem>([], { deep: false });

	/** custom spec to add as an option for grouping */
	readonly customGroupSpec?: Spec;

	/** check if dropdown values has been generated. If already generated, we don't want to generated again */
	dropDownValuesNotGenerated: boolean = true;

	private _groupingList = observable.array<GroupingLevel>([]);

	/** specify expand by which row attribute */
	@observable.ref rowAttributeGroupingLevel: ComponentItem;

	/** specify expand by which column attribute */
	@observable.ref columnAttributeGroupingLevel: ComponentItem;

	/** deteremines if expanding all the groups */
	@observable expandAllGroups: boolean;

	readonly isGroupOptionEnabled: boolean;

	/** return the list of grouping levels */
	get groupingList(): IObservableArray<GroupingLevel> {
		return this._groupingList;
	}

	/** used to conditionally disable `groupHeaderWidth` number input in `GalleryCustomViewOptionsStore` */
	get isGalleryGrouping(): boolean {
		return !!(this.columnAttributeGroupingLevel?.id || this.rowAttributeGroupingLevel?.id);
	}

	@computed get options(): ComponentItem[] {
		return this._options?.filter((option) => option.id !== this.rowAttributeGroupingLevel?.id && option.id !== this.columnAttributeGroupingLevel?.id);
	}

	/** return group options or an empty array, purpose is to prevent multiple searches or breaking the component */
	groupOptions = (item: ComponentItem): ComponentItem[] => {
		let newOptions = this.options;
		if (item.id != "") {
			newOptions = this.options.concat(item);
		}
		return organizeComponentItems(newOptions);
	};

	/** return current group settings */
	get currentGroupSettings(): ICustomViewGroup {
		const { columnAttributeGroupingLevel, expandAllGroups, groupingList, rowAttributeGroupingLevel } = this;

		if (groupingList.length != 0 || columnAttributeGroupingLevel != null || rowAttributeGroupingLevel != null) {
			return {
				key1: groupingList[0]?.item?.id || columnAttributeGroupingLevel?.id,
				key2: groupingList[1]?.item?.id || rowAttributeGroupingLevel?.id,
				expand1: groupingList[0]?.expand || expandAllGroups,
				expand2: groupingList[1]?.expand || expandAllGroups,
			};
		}
		return {};
	}

	/** returns whether the level should be disabled */
	isLevelDisabled = (index: number): boolean => {
		return index != 0 && !this.groupingList[0].expand;
	};

	getRowOrColumnAttributeGroupinglevel = (index: number): ComponentItem => {
		return index == 0 ? this.columnAttributeGroupingLevel : this.rowAttributeGroupingLevel;
	};

	/** fetch for data only when the dropdown is clicked and save the data as options */
	generateDropDownValues = async(): Promise<void> => {
		const {
			attributeStore,
			currentGroupSettings: { key1, key2 },
			customGroupSpec,
			dropDownValuesNotGenerated,
			setOptions,
		} = this;

		if (dropDownValuesNotGenerated) {
			setOptions(attributeStore.mainLevel.groupingAttributes(key1, key2, customGroupSpec));		
			this.dropDownValuesNotGenerated = false;
		}
	};

	/** drop item dnd */
	@action dropItem = (item: GroupingLevel, monitor: DropTargetMonitor, targetIndex: number, groupId: string, list: GroupingLevel[]): void => {
		const { groupingList } = this;
		if (groupingList.length > 1) {
			const secondLevelExpand = groupingList[1].expand;
			list[0].setExpandByDefault(groupingList[0].expand);
			list[1].setExpandByDefault(secondLevelExpand);
			groupingList.splice(0, groupingList.length);
			list.forEach((groupingLevel) => groupingList.push(groupingLevel));
		}
	};

	@action addGroupingLevel = (): void => {
		this._groupingList.push(new GroupingLevel());
	};

	@action removeGroupingLevel = (index: number): void => {
		const { _groupingList, _options } = this;
		const { item } = _groupingList[index];

		if (item.id) {
			_options.push(item);
		}

		_groupingList.splice(index, 1);
	};

	@action setGroupingLevel = (item: ComponentItem, index: number): void => {
		const { _groupingList, _options } = this;		
		const optionIndex = _options.indexOf(item);
		_options.splice(optionIndex, 1);
		if (_groupingList[index].item.id !== "") {
			_options.push(_groupingList[index].item);
		}
		_groupingList[index].item = item;
	};

	@action addRowAttributeGroupingLevel = (): void => {
		this.rowAttributeGroupingLevel ||= BLANK_COMPONENTITEM;
	};

	@action addColumnAttributeGroupingLevel = (): void => {
		this.columnAttributeGroupingLevel ||= BLANK_COMPONENTITEM;
	};

	@action setRowAttributeGroupingLevel = (item: ComponentItem): void => {
		this.rowAttributeGroupingLevel = item;
	};

	@action setColumnAttributeGroupingLevel = (item: ComponentItem): void => {
		this.columnAttributeGroupingLevel = item;
	};

	@action removeRowOrColumnLevel = (index: number): void => {
		if (index === 0) {
			this.columnAttributeGroupingLevel = null;
		} else {
			this.rowAttributeGroupingLevel = null;
		} 
	};

	/** set expand on level and check if the first level is marked expand. If not, set second level expand to false */
	@action checkExpandRules = (value: boolean, index: number): void => {
		this._groupingList[index].setExpandByDefault(value);
		if (index === 0 && !value) {
			this._groupingList[1]?.setExpandByDefault(false);
		}
	};

	@action setOptions = (options: ComponentItem[]): void => {
		this._options.push(...options);
	};

	@action setExpandAllGroups = (event: ChangeEvent<HTMLInputElement>, value: boolean): void => {
		this.expandAllGroups = value;
	};

	constructor(
		groupSettings: ICustomViewGroup,
		attributeStore: CustomViewAttributesStore,
		customGroupSpec: Spec,
		isGroupOptionEnabled: boolean,
	) {
		const { convertSpecToComponentItemForCustomView, mainLevel } = attributeStore;
		const { getSpecForKey } = mainLevel.specBuilder;
		const getItem = (key: C8AttributeKey): ComponentItem => {
			const spec = (key === customGroupSpec?.uniqueId) ? customGroupSpec : getSpecForKey(key);
			return convertSpecToComponentItemForCustomView(spec);
		};

		makeObservable(this);
		this.attributeStore = attributeStore;
		this.customGroupSpec = customGroupSpec;
		this.isGroupOptionEnabled = isGroupOptionEnabled;

		if (groupSettings) {
			const { expand1 = false, expand2 = false, key1, key2 } = groupSettings;

			if (isGroupOptionEnabled) {
				if (key1) {
					this.addColumnAttributeGroupingLevel();
					this.setColumnAttributeGroupingLevel(getItem(key1));
				}
				
				if (key2) {
					this.addRowAttributeGroupingLevel();
					this.setRowAttributeGroupingLevel(getItem(key2));
				}		
				this.expandAllGroups = expand1 && expand2;
			} else {
				if (key1) {
					this.addGroupingLevel();
					this._groupingList[0].setExpandByDefault(expand1);
					this.setGroupingLevel(getItem(key1), 0);
				}

				if (key2) {
					this.addGroupingLevel();
					this._groupingList[1].setExpandByDefault(expand2);
					this.setGroupingLevel(getItem(key2), 1);
				}
			}
		}
	}
}

export const CustomViewGroupingStoreContext = createContext<CustomViewGroupingStore>(undefined);
export const useCustomViewGroupingStore = (): CustomViewGroupingStore => useContext(CustomViewGroupingStoreContext);